import Vue from "vue";
import en from "vee-validate/dist/locale/en";
import isURL from "validator/lib/isURL";
import {
	alpha,
	alpha_num,
	alpha_spaces,
	email,
	length,
	min,
	min_value,
	numeric,
	required
} from "vee-validate/dist/rules";
import { configure, extend } from "vee-validate";

const nativeRules = {
	alpha,
	alpha_num,
	alpha_spaces,
	email,
	length,
	min,
	min_value,
	numeric,
	required
};

const customRules = {
	alphaNumSpaces: {
		message: "May only contain alpha-numeric characters as well as spaces.",

		validate( value ) {
			return /^[a-z\d\s]+$/i.test( value );
		}
	},

	money: {
		message: "Must be a valid dollar amount",

		validate( value ) {
			return /^-?[0-9]+(\.[0-9]{1,2})?$/.test( value );
		}
	},

	lowerAlphaUnderscore: {
		message: "May only contain lowercase letters and underscores",

		validate( value ) {
			return /^[a-z_]+$/.test( value );
		}
	},

	lowerAlphaDash: {
		message: "May only contain lowercase letters and dashes",

		validate( value ) {
			return /^[a-z-]+$/.test( value );
		}
	},

	validPassword: {
		message: " ",

		params: [ {
			name: "returnDetails",
			cast: Boolean
		} ],

		validate( value, { returnDetails } ) {
			const hasCapitalLetter = customRules.hasCapitalLetter.validate( value );
			const hasLowercaseLetter = customRules.hasLowercaseLetter.validate( value );
			const hasNumber = customRules.hasNumber.validate( value );
			const isMin = min.validate( value, [ 6 ] );

			if ( returnDetails ) {
				return {
					hasCapitalLetter,
					hasLowercaseLetter,
					hasNumber,
					min: isMin
				};
			}

			return hasCapitalLetter && hasLowercaseLetter && hasNumber && isMin;
		}
	},

	emailAvailable: {
		message: "This email is already being used",

		validate( value ) {
			const emailTrimmed = value.trim();

			return Vue.axios.get( "users/available", { params: { email: emailTrimmed } } )
			.then( response => ( { valid: response.data } ) )
			.catch( () => ( { valid: true } ) );
		}
	},

	nameAvailable: {
		message: "This first and last name combination are already being used",

		validate( value ) {
			const splitName = value.trim().split( " " );
			const firstName = splitName[0];
			const lastName = splitName[1];

			return Vue.axios.get( "users/available", {
				params: {
					firstName,
					lastName
				}
			} )
			.then( response => ( { valid: response.data } ) )
			.catch( () => ( { valid: true } ) );
		}
	},

	numberOfWords: {
		message( field, { numWords } ) {
			return `Must be ${numWords} ${numWords === 1 ? "word" : "words"}`;
		},

		params: [ {
			name: "numWords",
			cast: parseInt
		} ],

		validate( value, { numWords } ) {

			// Filter out extra spaces
			const words = value.split( " " ).filter( e => String( e ).trim() );

			return words.length === numWords;
		}
	},

	hasCapitalLetter: {
		message: "Must have a capital letter",

		validate( value ) {
			return /[A-Z]/.test( value );
		}
	},

	hasLowercaseLetter: {
		message: "Must have a lowercase letter",

		validate( value ) {
			return /[a-z]/.test( value );
		}
	},

	hasNumber: {
		message: "Must have a number",

		validate( value ) {
			return /\d/.test( value );
		}
	},

	phone: {
		message: "Must be a valid 10-digit phone number",

		validate( value ) {
			return /^\d{10}$/.test( value );
		}
	},

	url: {
		message( field, { require_protocol } ) {
			if ( require_protocol ) {
				return "Must be a valid URL and include a protocol (i.e. http://)";
			}

			return "Must be a valid URL";
		},

		params: [ {
			name: "require_protocol",
			cast: Boolean
		} ],

		validate( value, params ) {
			if ( Array.isArray( value ) ) {
				return value.every( val => isURL( val, params ) );
			}

			return isURL( value, params );
		}
	}
};


Object.keys( nativeRules ).forEach( rule => {
	extend( rule, {
		...nativeRules[rule],
		message: en.messages[rule]
	} );
} );

Object.keys( customRules ).forEach( rule => {
	extend( rule, customRules[rule] );
} );

configure( {
	mode: "eager",
	useConstraintAttrs: false
} );
